import { themes } from '@design';
import fontsCss from '@static/fonts/fonts.css?raw';
import baseCss from '@static/styles/base.css?raw';
import resetCss from '@static/styles/reset.css?raw';
import variablesCss from '@static/styles/variables.css?raw';
import { useThemeStore } from '@terminal/components/theme-controller/stores/useThemeStore';
import { isDarkTheme } from '@terminal/components/theme-controller/utils/theme-utils/theme-utils';
import { config } from '@terminal/config';
import {
  getBrandedPdfContent,
  PDF_WATERMARK_PRINT_CSS,
} from '@terminal/utils/pdf-branding/pdf-branding';

import { extractStylesheetRules, getCanvasDataUrl } from './fetchStylesUtils';

interface HandleDownloadDrawerArgs {
  contentToPrint: HTMLDivElement;
  entityTag: string | null;
  expandAccordions?: (expand: boolean) => void;
  title: string;
}

export const handleDownloadDrawer = ({
  contentToPrint,
  entityTag,
  expandAccordions,
  title,
}: HandleDownloadDrawerArgs) => {
  expandAccordions?.(true);

  setTimeout(async () => {
    try {
      const canvases = contentToPrint.querySelectorAll('canvas');
      const canvasImages: {
        canvas: HTMLCanvasElement;
        img: HTMLImageElement;
      }[] = [];
      const shouldInvertCanvases = isDarkTheme(
        useThemeStore.getState().themeName
      );

      // Convert all canvas elements to images before capturing to ensure position/styles are correct
      canvases.forEach(canvas => {
        const img = document.createElement('img');
        img.src = getCanvasDataUrl(canvas, shouldInvertCanvases);

        const computedStyle = window.getComputedStyle(canvas);
        img.style.cssText = computedStyle.cssText;

        img.style.position = computedStyle.position;
        img.style.top = computedStyle.top;
        img.style.left = computedStyle.left;
        img.style.width = computedStyle.width;
        img.style.height = computedStyle.height;
        img.style.pointerEvents = computedStyle.pointerEvents;

        img.className = canvas.className;

        img.width = canvas.width;
        img.height = canvas.height;

        canvas.style.display = 'none';
        canvas.parentNode?.insertBefore(img, canvas);

        canvasImages.push({ canvas, img });
      });

      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = contentToPrint.innerHTML;

      // Fix accordion heights - remove inline height styles on expanded accordions
      const accordionWrappers = tempContainer.querySelectorAll(
        '[data-testid="transition-wrapper"]'
      );
      accordionWrappers.forEach(wrapper => {
        if (wrapper instanceof HTMLElement && wrapper.style.height !== '0px') {
          wrapper.style.height = 'auto';
        }
      });

      const logoImages = tempContainer.querySelectorAll(
        'img[alt="logo-image"]'
      );
      logoImages.forEach(img => {
        if (img instanceof HTMLImageElement && img.src) {
          img.src = img.src.replace(/\/light\.png$/, '/dark.png');
        }
      });

      const htmlContent = getBrandedPdfContent(
        config.urls.root,
        tempContainer.innerHTML
      );

      canvasImages.forEach(({ canvas, img }) => {
        canvas.style.display = '';
        img.remove();
      });

      const lightThemeCss = Object.entries(themes.light_default)
        .map(([key, value]) => `${key}: ${value};`)
        .join('\n          ');

      const browserStylesheetStyles = (
        await Promise.all(
          Array.from(document.styleSheets).map(stylesheet =>
            extractStylesheetRules(stylesheet)
          )
        )
      )
        .filter(Boolean)
        .join('\n');

      const pdfTitle = `${title} - ${entityTag || 'Report'}`;

      const printDocument = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title> ${pdfTitle}</title>
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/flag-icons/css/flag-icons.min.css">
            <style>
              * {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }

              /* Static CSS files */
              ${resetCss}
              ${variablesCss}
              ${baseCss}
              ${fontsCss}
              
              /* Stylesheets extracted from browser's stylesheet API (same-origin via cssRules, cross-origin via fetch) */
              ${browserStylesheetStyles}
                            
              :root {
                ${lightThemeCss}
              }

              @media print {
                [data-print-scrollable] {
                  max-height: none;
                  overflow: visible;
                  height: auto;
                }

                ${PDF_WATERMARK_PRINT_CSS}
              }
            </style>
          </head>
          <body>
            <div>
              ${htmlContent}
            </div>
          </body>
        </html>
      `;

      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;

      if (iframeDoc) {
        const blob = new Blob([printDocument], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        iframe.src = url;

        iframe.onload = () => {
          const originalTitle = document.title;
          document.title = pdfTitle;

          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          iframe.contentWindow?.addEventListener('afterprint', () => {
            document.title = originalTitle;
            URL.revokeObjectURL(url);
            document.body.removeChild(iframe);
          });
        };
      }
    } finally {
      expandAccordions?.(false);
    }
  }, 500);
};
